import { Collapse, Tag, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { ReactNode } from 'react';
import type { App } from '../../types';
import { getAutoColor, getTextColorForBackground, lightenColor, normalizeHexColor } from './AppCard';

const { Text } = Typography;

function formatTrialEnds(value?: string | null) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return `Trial ends ${date.toLocaleDateString()}`;
}

function formatTrialEnded(value?: string | null) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return `Trial ended ${date.toLocaleDateString()}`;
}

/** Generate initials from app name (always 3 letters, letters only) */
function getInitials(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .map((w) => w.replace(/[^a-zA-Z]/g, ''))
    .filter((w) => w.length > 0);
  if (words.length === 0) return '?';
  if (words.length === 1) {
    return words[0].substring(0, 3).toUpperCase();
  }
  if (words.length === 2) {
    return (words[0][0] + words[1][0] + (words[1][1] ?? '')).toUpperCase();
  }
  return words
    .slice(0, 3)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

type AppGroupProps = {
  parent: App;
  subscribed: boolean;
  expandByDefault?: boolean;
  actions: ReactNode;
  children: ReactNode;
};

export default function AppGroup({
  parent,
  subscribed,
  expandByDefault = false,
  actions,
  children,
}: AppGroupProps) {
  const tags: { text: string; color: string }[] = [];
  if (!parent.isCatalogGroup && subscribed && !parent.isFree) {
    if (parent.isTrial) {
      tags.push({ text: 'TRIAL', color: 'blue' });
    } else {
      tags.push({ text: 'SUBSCRIBED', color: 'green' });
    }
  }
  if (!parent.isCatalogGroup && parent.hasOrgSubscription && !subscribed) {
    tags.push({ text: 'ORG SUB', color: 'purple' });
  }
  if (!parent.isCatalogGroup && !subscribed && parent.trialEndedAt && !parent.isFree) {
    tags.push({ text: 'TRIAL ENDED', color: 'red' });
  }
  if (!parent.isCatalogGroup && parent.isFree) {
    tags.push({ text: 'FREE', color: 'cyan' });
  }
  if (!parent.isCatalogGroup && parent.isAnneaOnly) {
    tags.push({ text: 'ANNEA', color: 'gold' });
  }

  const baseColor = normalizeHexColor(parent.iconColor) || getAutoColor(parent.id);
  const initials = getInitials(parent.name);
  const initialsColor = getTextColorForBackground(baseColor);
  const trialEndsText = !parent.isCatalogGroup && parent.isTrial ? formatTrialEnds(parent.trialEndsAt) : null;
  const trialEndedText = !parent.isCatalogGroup && !parent.isTrial ? formatTrialEnded(parent.trialEndedAt) : null;

  return (
    <Collapse
      defaultActiveKey={subscribed || expandByDefault ? [parent.id] : []}
      style={{ borderRadius: 12, overflow: 'hidden' }}
      items={[
        {
          key: parent.id,
          label: (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 8, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1, overflow: 'hidden' }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: `linear-gradient(135deg, ${baseColor}, ${lightenColor(baseColor)})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: initialsColor,
                    fontSize: initials.length > 2 ? 11 : 13,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    userSelect: 'none',
                    flexShrink: 0,
                  }}
                >
                  {initials}
                </div>
                <Text strong style={{ fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{parent.name}</Text>
                {parent.infoUrl && (
                  <Tooltip title="More info" color="rgba(0,0,0,0.65)">
                    <InfoCircleOutlined
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(parent.infoUrl!, '_blank', 'noopener,noreferrer');
                      }}
                      style={{ color: '#bfbfbf', fontSize: 14, cursor: 'pointer', flexShrink: 0 }}
                    />
                  </Tooltip>
                )}
                {tags.map((tag) => (
                  <Tag
                    key={tag.text}
                    color={tag.color}
                    style={{ margin: 0, fontSize: 10, lineHeight: '18px', padding: '0 6px', flexShrink: 0 }}
                  >
                    {tag.text}
                  </Tag>
                ))}
                {trialEndsText && (
                  <Text type="secondary" style={{ fontSize: 12, flexShrink: 0 }}>
                    {trialEndsText}
                  </Text>
                )}
                {trialEndedText && (
                  <Text type="danger" style={{ fontSize: 12, flexShrink: 0 }}>
                    {trialEndedText}
                  </Text>
                )}
              </div>
              <div style={{ flexShrink: 0 }}>
                {actions}
              </div>
            </div>
          ),
          children,
        },
      ]}
    />
  );
}
